import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { usePublishWebsite, useUpsertKanal } from '../../api/stellenausschreibungen';
import { BedarfStatusConfig } from '../../types/bedarf';
import { StellenanzeigeLinkModal } from './StellenanzeigeLinkModal';
import {
  AUSSCHREIBUNG_KANAELE,
  AUSSCHREIBUNG_PUBLISH_SPALTEN,
  AusschreibungCard as CardType,
  AusschreibungKanal,
  AusschreibungKanalState,
  AusschreibungScope,
  AusschreibungSpalte,
  KanalConfig,
  StellenanzeigeStatus,
  formatCardTitle,
} from '../../types/stellenausschreibung';

const ICON_KLASSE = 'h-3.5 w-3.5';

/** Kette — grau ohne Link, teal mit. */
function LinkIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      className={`${ICON_KLASSE} ${filled ? 'text-primary-600' : 'text-gray-300'}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5"
      />
    </svg>
  );
}

/** Aufwärtspfeil aus der Wolke — „das geht jetzt raus". */
function PublishIcon() {
  return (
    <svg
      className="h-3 w-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 16a4 4 0 01-.88-7.9A5 5 0 1115.9 6H16a5 5 0 011 9.9M12 12v9m0-9l-3 3m3-3l3 3"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      className={`${ICON_KLASSE} text-gray-400`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg
      className={`${ICON_KLASSE} text-gray-400`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}

/** Kurzform für die Karte; der ausformulierte Satz gehört in die E-Mail-Vorlage. */
function formatStunden(stunden: number | null): string | null {
  if (!stunden) return null;
  return `${String(stunden).replace('.', ',')} Std./Woche`;
}

function formatDatum(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE');
}

function kanalTooltip(state: AusschreibungKanalState): string {
  if (state.status === StellenanzeigeStatus.VEROEFFENTLICHT) {
    const wer = state.publishedBy
      ? ` von ${state.publishedBy.firstName} ${state.publishedBy.lastName}`
      : '';
    return `Veröffentlicht am ${formatDatum(state.publishedAt)}${wer}`;
  }
  if (state.status === StellenanzeigeStatus.DEAKTIVIERT) {
    const wer = state.deactivatedBy
      ? ` von ${state.deactivatedBy.firstName} ${state.deactivatedBy.lastName}`
      : '';
    return `Abgeschaltet am ${formatDatum(state.deactivatedAt)}${wer}`;
  }
  return 'Noch nicht ausgeschrieben';
}

interface Props {
  card: CardType;
  websitePublishVerfuegbar: boolean;
}

export function AusschreibungCard({ card, websitePublishVerfuegbar }: Props) {
  const upsert = useUpsertKanal();
  const publish = usePublishWebsite();
  const [pending, setPending] = useState<AusschreibungKanal | null>(null);
  const [linkModalKanal, setLinkModalKanal] = useState<AusschreibungKanal | null>(null);

  // Amber umrandet, weil hier Arbeit liegt — die Bedeutung des Hakens ändert das
  // aber nicht. Er heißt in jeder Spalte dasselbe: „steht online".
  const zuDeaktivieren = card.spalte === AusschreibungSpalte.ZU_DEAKTIVIEREN;
  const readOnly = card.spalte === AusschreibungSpalte.ERLEDIGT;

  const titel = formatCardTitle(card);
  const detailLink =
    card.scope === AusschreibungScope.BEDARF
      ? `/bedarfe/${card.refId}`
      : `/bedarfe?schoolCityId=${card.refId}`;

  const stateOf = (kanal: AusschreibungKanal) =>
    card.kanaele.find((k) => k.kanal === kanal)!;

  /**
   * Ein Haken bedeutet überall dasselbe: auf diesem Kanal steht eine Anzeige.
   * Setzen heißt veröffentlichen, Wegnehmen heißt abschalten — auch in
   * „Zu deaktivieren", wo man sonst leere Kästchen anhaken müsste, um etwas
   * offline zu nehmen.
   */
  const handleToggle = async (state: AusschreibungKanalState, checked: boolean) => {
    const label = KanalConfig[state.kanal].label;
    setPending(state.kanal);
    try {
      const antwort = await upsert.mutateAsync({
        scope: card.scope,
        refId: card.refId,
        kanal: state.kanal,
        status: checked
          ? StellenanzeigeStatus.VEROEFFENTLICHT
          : StellenanzeigeStatus.DEAKTIVIERT,
        // Der Link überlebt das Abschalten: er ist die Spur, unter der die
        // Anzeige stand, und wird beim Wiederveröffentlichen gebraucht.
        url: state.url ?? undefined,
      });
      const website = antwort.meta?.website;
      if (website === 'FEHLGESCHLAGEN') {
        // Der Haken steht, die Anzeige aber womöglich auch noch — und genau das
        // ist der Zustand, den diese Seite verhindern soll.
        toast.warning(
          `${label} abgehakt, aber die Anzeige konnte auf der Website nicht zurückgezogen werden — bitte dort nachsehen`,
        );
      } else if (website === 'ZURUECKGEZOGEN') {
        toast.success(`${label} abgeschaltet — Anzeige zurückgezogen`);
      } else {
        toast.success(checked ? `${label} veröffentlicht` : `${label} abgeschaltet`);
      }
    } catch (error) {
      console.error('Kanal-Update fehlgeschlagen:', error);
      toast.error('Konnte nicht gespeichert werden');
    } finally {
      setPending(null);
    }
  };

  /**
   * Ein Klick legt die Anzeige auf www.juniva.de an, hakt den Kanal ab und
   * hinterlegt den Link — alles serverseitig in einem Zug. Ohne Rückfrage, weil
   * der Knopf selbst die Zusage ist; und ein zweiter Klick wäre harmlos, denn
   * die Jobs-API erkennt die Anzeige an der Id wieder.
   */
  const handlePublishWebsite = async () => {
    setPending(AusschreibungKanal.WEBSITE);
    try {
      const antwort = await publish.mutateAsync({
        scope: card.scope,
        refId: card.refId,
      });
      toast.success(
        antwort.meta.aktion === 'aktualisiert'
          ? 'Anzeige auf der Website aktualisiert'
          : 'Anzeige auf der Website veröffentlicht',
      );
      // Die API meldet Kleinigkeiten, die sie selbst geradegebogen hat. Sie sind
      // kein Fehler, aber jemand sollte sie gesehen haben.
      for (const warnung of antwort.meta.warnungen ?? []) toast.info(warnung);
    } catch (err: any) {
      // Die Meldung des Servers benennt meistens die Lücke im Bedarf („Ohne Ort
      // der Schule …") — die hilft weiter, ein Statuscode nicht.
      toast.error(
        err?.response?.data?.message ||
          'Die Anzeige konnte nicht veröffentlicht werden',
      );
    } finally {
      setPending(null);
    }
  };

  const handleCopy = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link zu ${label} kopiert`);
    } catch {
      // Ohne sicheren Kontext gibt es keine Zwischenablage. Die Adresse steht im
      // Tooltip und hinter dem Öffnen-Knopf, also ist nichts verloren.
      toast.error('Kopieren nicht möglich — Link über „Öffnen" erreichbar');
    }
  };

  const handleLinkSave = async (url: string) => {
    if (!linkModalKanal) return;
    const state = stateOf(linkModalKanal);
    setPending(linkModalKanal);
    try {
      await upsert.mutateAsync({
        scope: card.scope,
        refId: card.refId,
        kanal: linkModalKanal,
        // Der Status bleibt, wie er ist — hier wird nur der Link geändert.
        status: state.status ?? StellenanzeigeStatus.VEROEFFENTLICHT,
        url,
      });
      toast.success(url ? 'Link gespeichert' : 'Link entfernt');
    } finally {
      setPending(null);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg border p-3 shadow-sm ${
        zuDeaktivieren ? 'border-amber-300' : 'border-gray-200'
      }`}
      data-testid="ausschreibung-card"
      data-ref-id={card.refId}
      data-spalte={card.spalte}
    >
      {/* Der Name bekommt die ganze Zeile. Nebeneinander mit einem Badge wie
          „Kennenlerngespräch" bliebe auf einer 240px-Karte zu wenig davon übrig. */}
      <Link
        to={detailLink}
        className="block font-medium text-gray-900 hover:text-primary-600 truncate"
      >
        {titel}
      </Link>

      {card.bedarf ? (
        <div className="mt-1 text-xs text-gray-600 space-y-0.5">
          {card.bedarf.schoolName && <div className="truncate">{card.bedarf.schoolName}</div>}
          {/* Umbrechen statt abschneiden: „Kennenlerngespräch" neben Ort und
              Stunden passt auf eine 240px-Karte nicht immer, und dann soll das
              Badge eine Zeile tiefer rutschen, nicht der Ort verschwinden. */}
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span>
              {[card.ortName, formatStunden(card.bedarf.wochenstunden)]
                .filter(Boolean)
                .join(' · ') || '—'}
            </span>
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                BedarfStatusConfig[card.bedarf.status].bgColor
              } ${BedarfStatusConfig[card.bedarf.status].color}`}
            >
              {BedarfStatusConfig[card.bedarf.status].label}
            </span>
          </div>
        </div>
      ) : (
        <div className="mt-1 text-xs text-gray-600">
          <Link to={detailLink} className="hover:text-primary-600">
            {card.ort?.aktiveBedarfe === 1
              ? '1 aktiver Bedarf'
              : `${card.ort?.aktiveBedarfe ?? 0} aktive Bedarfe`}
          </Link>
        </div>
      )}

      <ul className="mt-3 space-y-1.5 border-t border-gray-100 pt-2">
        {AUSSCHREIBUNG_KANAELE.map((kanal) => {
          const state = stateOf(kanal);
          const checked = state.status === StellenanzeigeStatus.VEROEFFENTLICHT;
          const isPending = pending === kanal;
          const label = KanalConfig[kanal].label;

          return (
            <li key={kanal} className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={checked}
                disabled={isPending || readOnly}
                onChange={(e) => handleToggle(state, e.target.checked)}
                aria-label={`${label} veröffentlicht`}
                className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
              <span
                className={`flex-1 truncate ${
                  checked ? 'text-gray-900' : 'text-gray-500'
                }`}
                title={kanalTooltip(state)}
              >
                {label}
              </span>

              {/* Kopieren und Öffnen erscheinen erst, wenn es etwas zu kopieren
                  und zu öffnen gibt — sonst wären es zwei tote Knöpfe. */}
              {state.url && (
                <>
                  <button
                    type="button"
                    onClick={() => handleCopy(state.url!, label)}
                    title="Link kopieren"
                    aria-label={`Link für ${label} kopieren`}
                    className="shrink-0 rounded p-0.5 hover:bg-gray-100"
                  >
                    <CopyIcon />
                  </button>
                  <a
                    href={state.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Anzeige in neuem Tab öffnen"
                    aria-label={`Anzeige für ${label} öffnen`}
                    className="shrink-0 rounded p-0.5 hover:bg-gray-100"
                  >
                    <ExternalIcon />
                  </a>
                </>
              )}

              {/* Nur dort, wo die Stelle wirklich zu besetzen ist: in
                  „Zu deaktivieren" wäre Veröffentlichen die Umkehrung dessen,
                  was die Spalte verlangt. */}
              {kanal === AusschreibungKanal.WEBSITE &&
                !checked &&
                websitePublishVerfuegbar &&
                AUSSCHREIBUNG_PUBLISH_SPALTEN.includes(card.spalte) && (
                  <button
                    type="button"
                    onClick={handlePublishWebsite}
                    disabled={isPending}
                    title="Anzeige auf www.juniva.de erstellen und veröffentlichen"
                    className="shrink-0 inline-flex items-center gap-1 rounded border border-primary-200 bg-primary-50 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 hover:bg-primary-100 disabled:opacity-50"
                  >
                    {isPending ? (
                      <span
                        className="h-3 w-3 animate-spin rounded-full border-b-2 border-primary-600"
                        aria-hidden="true"
                      />
                    ) : (
                      <PublishIcon />
                    )}
                    Veröffentlichen
                  </button>
                )}

              {state.status && !readOnly && (
                <button
                  type="button"
                  onClick={() => setLinkModalKanal(kanal)}
                  title={state.url ?? 'Link hinterlegen'}
                  aria-label={`Link für ${label} ${state.url ? 'bearbeiten' : 'hinterlegen'}`}
                  className="shrink-0 rounded p-0.5 hover:bg-gray-100"
                >
                  <LinkIcon filled={!!state.url} />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {linkModalKanal && (
        <StellenanzeigeLinkModal
          isOpen
          onClose={() => setLinkModalKanal(null)}
          onSave={handleLinkSave}
          isPending={pending === linkModalKanal}
          kanalLabel={KanalConfig[linkModalKanal].label}
          titel={titel}
          currentUrl={stateOf(linkModalKanal).url}
        />
      )}
    </div>
  );
}
